import { DialogComponent } from '@theme/dialog';
import { fetchConfig } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Responsive in-cart variant editor.
 * Horizon's existing variant-picker owns option validity; this component only
 * coordinates the mobile sheet / desktop modal and the add-first/remove-old
 * cart replacement.
 */
class MobileCartVariantEditor extends DialogComponent {
  #abortController = new AbortController();
  #loadController = null;
  #line = null;
  #selectedVariant = null;

  connectedCallback() {
    super.connectedCallback();

    const signal = this.#abortController.signal;
    this.closest('.cart-items__wrapper')?.addEventListener('click', this.#handleCartClick, { signal });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.querySelectorAll('[data-mobile-cart-variant-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeDialog(), { signal });
    });
    this.#confirmButton?.addEventListener('click', this.#replaceVariant, { signal });
  }

  disconnectedCallback() {
    this.#loadController?.abort();
    this.#abortController.abort();
    super.disconnectedCallback();
  }

  get #pickerContainer() {
    return this.querySelector('[data-mobile-cart-variant-picker]');
  }

  get #productLabel() {
    return this.querySelector('[data-mobile-cart-variant-product]');
  }

  get #summaryImage() {
    return /** @type {HTMLImageElement | null} */ (
      this.querySelector('[data-mobile-cart-variant-image]')
    );
  }

  get #summaryPrice() {
    return this.querySelector('[data-mobile-cart-variant-price]');
  }

  get #summaryCompare() {
    return this.querySelector('[data-mobile-cart-variant-compare]');
  }

  get #summaryDiscount() {
    return this.querySelector('[data-mobile-cart-variant-discount]');
  }

  get #summarySelection() {
    return this.querySelector('[data-mobile-cart-variant-selection]');
  }

  get #status() {
    return this.querySelector('[data-mobile-cart-variant-status]');
  }

  get #error() {
    return this.querySelector('[data-mobile-cart-variant-error]');
  }

  get #confirmButton() {
    return /** @type {HTMLButtonElement | null} */ (
      this.querySelector('[data-mobile-cart-variant-confirm]')
    );
  }

  #handleCartClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = /** @type {HTMLButtonElement | null} */ (
      event.target.closest('[data-mobile-cart-variant-trigger]')
    );
    if (!trigger) return;

    event.preventDefault();
    this.#openForLine(trigger);
  };

  /** @param {HTMLButtonElement} trigger */
  async #openForLine(trigger) {
    this.#loadController?.abort();
    this.#loadController = new AbortController();

    let properties = {};
    try {
      properties = JSON.parse(trigger.dataset.properties || '{}');
    } catch (error) {
      console.warn('[mobile-cart-variant-editor] Invalid line properties:', error);
    }

    this.#line = {
      key: trigger.dataset.lineKey || '',
      productId: trigger.dataset.productId || '',
      productUrl: trigger.dataset.productUrl || '',
      productTitle: trigger.dataset.productTitle || '',
      variantId: trigger.dataset.variantId || '',
      variantTitle: trigger.dataset.variantTitle || '',
      quantity: Number(trigger.dataset.quantity) || 1,
      properties,
      sellingPlanId: trigger.dataset.sellingPlanId || '',
      imageUrl: trigger.dataset.imageUrl || '',
      price: Number(trigger.dataset.price) || 0,
      compareAtPrice: Number(trigger.dataset.compareAtPrice) || 0,
      discountPercentage: Number(trigger.dataset.discountPercentage) || 0,
      currencySymbol: trigger.dataset.currencySymbol || '',
    };
    this.#selectedVariant = null;
    this.#clearError();

    if (this.#productLabel) this.#productLabel.textContent = this.#line.productTitle;
    this.#syncSummary({
      title: this.#line.variantTitle,
      price: this.#line.price,
      compare_at_price: this.#line.compareAtPrice,
      featured_image: this.#line.imageUrl,
    });
    if (this.#status) this.#status.textContent = 'Loading options…';
    this.#pickerContainer?.replaceChildren();
    if (this.#confirmButton) this.#confirmButton.disabled = true;
    this.refs.dialog.setAttribute('aria-busy', 'true');
    this.showDialog();

    try {
      const productUrl = new URL(this.#line.productUrl, window.location.origin);
      productUrl.searchParams.set('variant', this.#line.variantId);

      const response = await fetch(productUrl, {
        headers: { Accept: 'text/html' },
        signal: this.#loadController.signal,
      });
      if (!response.ok) throw new Error(`Unable to load product options (${response.status})`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const picker = html.querySelector(
        `main[data-template^='product'] variant-picker[data-product-id="${this.#line.productId}"]`
      );
      if (!(picker instanceof HTMLElement)) throw new Error('Variant options are unavailable for this product.');

      const pickerClone = document.importNode(picker, true);
      this.#pickerContainer?.replaceChildren(pickerClone);

      const variantData = pickerClone.querySelector('script[type="application/json"]')?.textContent;
      if (!variantData) throw new Error('The current variant could not be loaded.');

      this.#selectedVariant = JSON.parse(variantData);
      this.#syncSelectionState();

      requestAnimationFrame(() => {
        pickerClone.querySelector('input:checked, option:checked')?.scrollIntoView({ block: 'center' });
      });
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.#showError(error?.message || 'Unable to load product options.');
    } finally {
      this.refs.dialog.removeAttribute('aria-busy');
    }
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    if (this.#confirmButton) this.#confirmButton.disabled = true;
    if (this.#status) this.#status.textContent = 'Checking availability…';
    this.#clearError();

    event.promise
      ?.then(({ detail }) => {
        this.#selectedVariant = detail?.resource ?? null;
        this.#syncSelectionState();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') this.#showError('Unable to update the selected variant.');
      });
  };

  #syncSelectionState() {
    const available = Boolean(this.#selectedVariant?.id) && this.#selectedVariant?.available !== false;
    if (this.#selectedVariant) this.#syncSummary(this.#selectedVariant);
    if (this.#confirmButton) this.#confirmButton.disabled = !available;
    if (this.#status) {
      this.#status.textContent = available
        ? this.#selectedVariant.title || ''
        : 'This variation is unavailable.';
    }
  }

  #syncSummary(variant) {
    if (!this.#line || !variant) return;

    const imageUrl =
      variant.featured_media?.preview_image?.src ||
      variant.featured_image?.src ||
      variant.featured_image ||
      this.#line.imageUrl;
    if (this.#summaryImage) {
      if (imageUrl) {
        this.#summaryImage.src = imageUrl;
        this.#summaryImage.hidden = false;
      } else {
        this.#summaryImage.removeAttribute('src');
        this.#summaryImage.hidden = true;
      }
    }

    const price = Number(variant.price ?? this.#line.price) || 0;
    const compareAtPrice = Number(variant.compare_at_price ?? this.#line.compareAtPrice) || 0;
    const hasDiscount = compareAtPrice > price;
    const discountPercentage = hasDiscount
      ? Math.floor(((compareAtPrice - price) * 100) / compareAtPrice)
      : 0;

    if (this.#summaryPrice) this.#summaryPrice.textContent = this.#formatMoney(price);
    if (this.#summaryCompare) {
      this.#summaryCompare.textContent = hasDiscount ? this.#formatMoney(compareAtPrice) : '';
      this.#summaryCompare.hidden = !hasDiscount;
    }
    if (this.#summaryDiscount) {
      this.#summaryDiscount.textContent = hasDiscount ? `-${discountPercentage}%` : '';
      this.#summaryDiscount.hidden = !hasDiscount;
    }
    if (this.#summarySelection) {
      this.#summarySelection.textContent = variant.title || this.#line.variantTitle;
    }
  }

  #formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    return `${this.#line?.currencySymbol || ''}${amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  #replaceVariant = async () => {
    if (!this.#line || !this.#selectedVariant?.id || this.#selectedVariant.available === false) return;

    if (String(this.#selectedVariant.id) === String(this.#line.variantId)) {
      await this.closeDialog();
      return;
    }

    const confirmButton = this.#confirmButton;
    if (confirmButton) confirmButton.disabled = true;
    this.refs.dialog.setAttribute('aria-busy', 'true');
    if (this.#status) this.#status.textContent = 'Updating cart…';
    this.#clearError();

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : ''))
      .filter(Boolean);
    const sections = [...new Set(sectionIds)].join(',');
    const commonRequest = {
      sections,
      sections_url: window.location.pathname,
    };

    const replacement = {
      id: Number(this.#selectedVariant.id),
      quantity: this.#line.quantity,
    };
    if (Object.keys(this.#line.properties).length > 0) replacement.properties = this.#line.properties;
    if (this.#line.sellingPlanId) replacement.selling_plan = Number(this.#line.sellingPlanId);

    const deferredUpdate = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [
          { id: this.#line.key, quantity: 0 },
          { merchandiseId: String(this.#selectedVariant.id), quantity: this.#line.quantity },
        ],
        promise: deferredUpdate.promise,
      })
    );

    let addResponse;
    try {
      // Add first. The original line remains untouched if Shopify rejects this request.
      addResponse = await this.#postCart(Theme.routes.cart_add_url, {
        items: [replacement],
        ...commonRequest,
      });
    } catch (error) {
      deferredUpdate.reject(error);
      this.#dispatchCartError(error);
      this.#showError(error?.message || 'The replacement variation could not be added.');
      this.refs.dialog.removeAttribute('aria-busy');
      if (confirmButton) confirmButton.disabled = false;
      return;
    }

    try {
      const changedCart = await this.#postCart(Theme.routes.cart_change_url, {
        id: this.#line.key,
        quantity: 0,
        ...commonRequest,
      });

      await this.closeDialog();
      deferredUpdate.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(changedCart),
        detail: {
          sections: changedCart.sections,
          items: changedCart.items,
          itemCount: changedCart.item_count,
          source: 'mobile-cart-variant-editor',
          didError: false,
        },
      });
    } catch (error) {
      // The new line exists, so render that truthful cart state. Do not attempt a
      // destructive rollback that could remove a pre-existing matching line.
      deferredUpdate.resolve({
        detail: {
          sections: addResponse?.sections,
          source: 'mobile-cart-variant-editor',
          didError: true,
        },
      });
      this.#dispatchCartError(error);
      this.#showError('The new variation was added, but the original line could not be removed. Please remove it manually.');
    } finally {
      this.refs.dialog.removeAttribute('aria-busy');
      if (confirmButton && this.isConnected) confirmButton.disabled = false;
    }
  };

  async #postCart(url, body) {
    const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
    let data;
    try {
      data = await response.json();
    } catch {
      throw new Error('Shopify returned an invalid cart response.');
    }

    if (!response.ok || data?.status || data?.errors) {
      throw new Error(data?.description || data?.message || data?.errors || 'Cart update failed.');
    }
    return data;
  }

  #dispatchCartError(error) {
    this.dispatchEvent(
      new CartErrorEvent({
        error: error?.message || 'Cart update failed',
        code: 'INVALID',
      })
    );
  }

  #clearError() {
    if (!this.#error) return;
    this.#error.textContent = '';
    this.#error.classList.add('hidden');
  }

  #showError(message) {
    if (this.#error) {
      this.#error.textContent = message;
      this.#error.classList.remove('hidden');
    }
    if (this.#status) this.#status.textContent = '';
  }
}

if (!customElements.get('mobile-cart-variant-editor')) {
  customElements.define('mobile-cart-variant-editor', MobileCartVariantEditor);
}
